import { Box, Grid, Paper, Skeleton, Pagination, Typography, Button, Tooltip } from '@mui/material'
import EmptyState from '../../components/EmptyState'
import { useDashboardTableDocuments } from '../../queries/hooks/useDashboardTableDocuments'
import { Link } from 'react-router-dom'
import PageContainer from '../../components/layout/PageContainer'
import useDashboardListFilters from '../../util/useDashboardListFilters'
import { getErrorMessage } from '../../util/errorHandling'
import EmptyPageContainer from '../../components/EmptyPageContainer'
import { formatLocalDateOnly } from '../../util/dateUtils'
import { getSeverityCount, getTotalSeverityCount } from '../../util/severityCounts'

const TILE_TITLE_HEIGHT = 28
const TILE_TEXT_HEIGHT = 20
const TILE_BUTTON_HEIGHT = 36
const TILE_MIN_HEIGHT = 250
const TILE_CONTAINER_SX = {
  border: '1px solid #ddd',
  p: 2,
  borderRadius: 1,
  minHeight: `${TILE_MIN_HEIGHT}px`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const
const TILE_LINE_SPACING_SX = { mb: 0.35 } as const

export const AlertsGrid = () => {
  const { filters, updateFilters } = useDashboardListFilters({
    page: 1,
    limit: 9,
  })

  const {
    data,
    isLoading: isDocumentsLoading,
    error: documentsError,
  } = useDashboardTableDocuments()

  const { results: documents, pagination } = data ?? {}

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    updateFilters({ page: value })
  }

  if (documentsError) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="error"
          title="Error loading the alerts"
          description={getErrorMessage(documentsError, 'Please try again later')}
        />
      </EmptyPageContainer>
    )
  }

  const documentResults = documents ?? []
  const totalDocuments = pagination?.total ?? documentResults.length
  const perPageLimit = pagination?.limit ?? filters.limit ?? 9
  const loadingTileCount = Math.max(1, perPageLimit)

  if (!isDocumentsLoading && documentResults.length === 0) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="empty"
          title="No alerts found"
          description="No alerts found for this organization"
        />
      </EmptyPageContainer>
    )
  }

  // Calculate total pages for pagination
  const totalPages = totalDocuments && perPageLimit ? Math.ceil(totalDocuments / perPageLimit) : 0

  // Show list of documents with alerts if no specific document is selected
  return (
    <PageContainer>
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" textAlign="center" gutterBottom>
            Documents with Alerts
          </Typography>
          {isDocumentsLoading ? (
            <Box display="flex" justifyContent="center" sx={{ mb: 1 }}>
              <Skeleton variant="text" width={180} height={20} />
            </Box>
          ) : (
            !!totalDocuments && (
              <Typography variant="body2" textAlign="center" color="text.secondary" gutterBottom>
                Showing {documentResults.length} of {totalDocuments} documents
              </Typography>
            )
          )}
        </Grid>
        {isDocumentsLoading &&
          Array.from(
            { length: loadingTileCount },
            (_, rowNumber) => `alerts-loading-${rowNumber + 1}`
          ).map((rowKey) => (
            <Grid item xs={12} sm={6} md={4} key={rowKey}>
              <Paper>
                <Box sx={TILE_CONTAINER_SX}>
                  <Box sx={TILE_LINE_SPACING_SX}>
                    <Skeleton variant="rounded" width="75%" height={TILE_TITLE_HEIGHT} />
                  </Box>
                  <Box sx={TILE_LINE_SPACING_SX}>
                    <Skeleton variant="rounded" width="60%" height={TILE_TEXT_HEIGHT} />
                  </Box>
                  <Box sx={TILE_LINE_SPACING_SX}>
                    <Skeleton variant="rounded" width="60%" height={TILE_TEXT_HEIGHT} />
                  </Box>
                  <Box sx={TILE_LINE_SPACING_SX}>
                    <Skeleton variant="rounded" width="55%" height={TILE_TEXT_HEIGHT} />
                  </Box>
                  <Box mt={1} mb={1}>
                    <Skeleton variant="rounded" width="70%" height={TILE_TEXT_HEIGHT} />
                  </Box>
                  <Box mt="auto">
                    <Skeleton variant="rounded" height={TILE_BUTTON_HEIGHT} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        {!isDocumentsLoading &&
          documentResults.map((point) => {
            const lowCount = getSeverityCount(point, '1')
            const mediumCount = getSeverityCount(point, '2')
            const criticalCount = getSeverityCount(point, '3')
            const totalCount = getTotalSeverityCount(point)

            let countsDivision = ''
            if (criticalCount > 0) {
              countsDivision += `${criticalCount} critical, `
            }
            if (mediumCount > 0) {
              countsDivision += `${mediumCount} medium, `
            }
            if (lowCount > 0) {
              countsDivision += `${lowCount} low, `
            }

            if (countsDivision.endsWith(', ')) {
              countsDivision = countsDivision.slice(0, -2)
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={point.documentId}>
                <Paper>
                  <Box sx={TILE_CONTAINER_SX}>
                    <Tooltip title={point.documentTitle} arrow>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        noWrap
                        sx={{
                          minHeight: `${TILE_TITLE_HEIGHT}px`,
                          lineHeight: `${TILE_TITLE_HEIGHT}px`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {point.documentTitle}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      noWrap
                      sx={{
                        minHeight: `${TILE_TEXT_HEIGHT}px`,
                        lineHeight: `${TILE_TEXT_HEIGHT}px`,
                      }}
                    >
                      Patient: {point.patientName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      noWrap
                      sx={{
                        minHeight: `${TILE_TEXT_HEIGHT}px`,
                        lineHeight: `${TILE_TEXT_HEIGHT}px`,
                      }}
                    >
                      Clinician: {point.clinicianName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      noWrap
                      sx={{
                        minHeight: `${TILE_TEXT_HEIGHT}px`,
                        lineHeight: `${TILE_TEXT_HEIGHT}px`,
                      }}
                    >
                      Date: {formatLocalDateOnly(point.documentAt)}
                    </Typography>
                    <Box mt={1} mb={1}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          minHeight: `${TILE_TEXT_HEIGHT}px`,
                          lineHeight: `${TILE_TEXT_HEIGHT}px`,
                        }}
                      >
                        Alerts: {totalCount} ({countsDivision})
                      </Typography>
                    </Box>
                    <Box mt="auto">
                      <Button
                        component={Link}
                        to={`/alerts?auditId=${point.documentId}`}
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ minHeight: `${TILE_BUTTON_HEIGHT}px` }}
                        data-qa-id={`alerts-view-document-button-${point.documentId}`}
                      >
                        View Document
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )
          })}

        {/* Pagination */}
        {isDocumentsLoading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Skeleton
                variant="rounded"
                width={320}
                height={40}
                sx={{ bgcolor: 'grey.300' }}
                data-qa-id="alerts-pagination-loading"
              />
            </Box>
          </Grid>
        ) : (
          totalPages > 1 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={filters.page ?? 1}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  data-qa-id="alerts-pagination"
                />
              </Box>
            </Grid>
          )
        )}
      </Grid>
    </PageContainer>
  )
}
